import { useRef, useEffect } from 'react';
import {
  getConversationFromHash,
  setConversationInHash,
  clearChatFromHash,
  getEmptyConversationFromStorage,
  clearEmptyConversationFlag
} from '../utils/conversationUtils';

/**
 * Manages conversation lifecycle: creation, selection, deletion, restoration
 */
export const useConversationManagement = ({
  isOpen,
  activeConversationId,
  conversationsList,
  createConversation,
  switchConversation,
  deleteConversation,
  loadUserConversations,
  conversations,
  chatInputRef
}) => {
  const isCreatingConversationRef = useRef(false);

  // Load conversations when component mounts
  useEffect(() => {
    if (isOpen && conversationsList.length === 0) {
      loadUserConversations();
    }
  }, [isOpen, conversationsList.length, loadUserConversations]);

  // Restore conversation from hash/localStorage on mount
  useEffect(() => {
    if (isOpen && !activeConversationId) {
      const restoreConversation = async () => {
        const hashConversationId = getConversationFromHash();
        const lastActive = localStorage.getItem('chat_lastActiveConversation');
        const emptyConversation = getEmptyConversationFromStorage();
        
        // Priority: hash > lastActive > emptyConversation
        const conversationToRestore = hashConversationId || lastActive || emptyConversation;
        
        if (conversationToRestore && conversationToRestore !== 'new') {
          try {
            await switchConversation(conversationToRestore);
            setConversationInHash(conversationToRestore);
            return;
          } catch (error) {
            console.warn("⚠️ Failed to restore, cleaning up:", error);
            clearChatFromHash();
            localStorage.removeItem('chat_lastActiveConversation');
            clearEmptyConversationFlag();
          }
        }
        
        // Check again for empty conversation before creating new one
        const stillHasEmpty = getEmptyConversationFromStorage();
        if (stillHasEmpty) {
          try {
            await switchConversation(stillHasEmpty);
            setConversationInHash(stillHasEmpty);
            return;
          } catch (error) {
            // If that fails, clear the flag and create new
            clearEmptyConversationFlag();
          }
        }

        // Only create if no empty conversation exists
        createConversation("");
      };
      
      restoreConversation();
    }
  }, [isOpen, activeConversationId]);

  // Handle conversation selection
  const handleConversationSelect = async (conversationId) => {
    try {
      setConversationInHash(conversationId); // Update hash immediately
      await switchConversation(conversationId);
      loadUserConversations(); // Refresh list to show updated state
    } catch (error) {
      console.error('❌ Failed to switch conversation:', error);
    }
  };

  // Handle new conversation creation
  const handleNewConversation = async () => {
    // Prevent simultaneous calls
    if (isCreatingConversationRef.current) {
      console.log('⏳ Already creating conversation, skipping...');
      return;
    }
    
    isCreatingConversationRef.current = true;
    
    try {
      const emptyConversation = getEmptyConversationFromStorage();
      
      // If we already have an empty conversation active, just focus input
      if (emptyConversation && emptyConversation === activeConversationId) {
        if (chatInputRef.current) {
          chatInputRef.current.focus();
        }
        return;
      }
      
      // If there's an empty conversation, try to switch to it
      if (emptyConversation && emptyConversation !== activeConversationId) {
        try {
          await switchConversation(emptyConversation);
          loadUserConversations(); // Refresh list
          return;
        } catch (error) {
          // Empty conversation was deleted - clear flag immediately and create new
          console.warn('⚠️ Stored empty conversation not found, clearing flag');
          clearEmptyConversationFlag();
          // Don't return, fall through to create new below
        }
      }
      
      // Create new empty conversation (either no empty existed or we cleared the bad one above)
      const conversationId = await createConversation("");
      if (conversationId) {
        setConversationInHash(conversationId);
        loadUserConversations(); // Refresh list
      } else {
        console.error('❌ createConversation returned no ID');
      }
    } catch (error) {
      console.error('❌ Failed in handleNewConversation:', error);
      // Clear any stale data
      clearEmptyConversationFlag();
    } finally {
      // Always reset the flag
      isCreatingConversationRef.current = false;
    }
  };

  // Handle conversation deletion
  const handleDeleteConversation = async (conversationId) => {
    try {
      // Check if deleting the empty conversation
      const emptyConv = getEmptyConversationFromStorage();
      if (emptyConv === conversationId) {
        clearEmptyConversationFlag();
      }
      
      await deleteConversation(conversationId);
      
      // If we deleted the active conversation, create a new empty one
      if (conversationId === activeConversationId) {
        const newConvId = await createConversation("");
        setConversationInHash(newConvId);
        loadUserConversations();
      }
    } catch (error) {
      console.error('❌ Failed to delete conversation:', error);
    }
  };

  return {
    handleConversationSelect,
    handleNewConversation,
    handleDeleteConversation
  };
};

